import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { LayoutDashboard, ArrowUpRight } from "lucide-react";

/**
 * DashboardSwitcher (Iter 77.4 · Dashboard-Level RBAC)
 *
 * Renders a compact chooser strip when the current user has **two or more**
 * dashboards available. If the user has only one dashboard (or zero), the
 * strip renders nothing — the user never sees a selector for a set of one.
 *
 * Backed by `GET /api/dashboards/available`, which is the source of truth
 * for which dashboards the current user can see. When
 * `ENABLE_DASHBOARD_RBAC=false` the backend returns the legacy-gated set
 * so this component degrades gracefully.
 */
export default function DashboardSwitcher() {
  const [dashboards, setDashboards] = useState(null);   // null = loading, [] = none
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    api.get("/dashboards/available")
      .then(({ data }) => {
        if (cancelled) return;
        setDashboards(Array.isArray(data?.dashboards) ? data.dashboards : []);
      })
      .catch(() => {
        if (!cancelled) setDashboards([]);
      });
    return () => { cancelled = true; };
  }, []);

  if (!dashboards || dashboards.length < 2) return null;

  return (
    <div
      data-testid="dashboard-switcher"
      className="bg-white border border-[#E5E5E0] rounded-2xl p-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <LayoutDashboard className="w-4 h-4 text-[#1E3A8A]" strokeWidth={1.75} />
        <span className="hw-eyebrow text-[#1E3A8A]">Your dashboards</span>
        <span className="text-xs text-[#64748B]">
          · {dashboards.length} available
        </span>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="dashboard-switcher-grid">
        {dashboards.map((d) => {
          const active = location.pathname === d.route
            || (d.route === "/executive" && location.pathname === "/executive-operations");
          return (
            <Link
              key={d.key}
              to={d.route}
              data-testid={`dashboard-switcher-${d.key}`}
              className={`group flex items-start justify-between gap-3 rounded-xl border px-3.5 py-3 transition-colors ${
                active
                  ? "bg-[#E8ECE7] border-[#7E937D]"
                  : "bg-white border-[#E5E5E0] hover:border-[#1E3A8A]/40 hover:bg-[#F9F8F6]"
              }`}
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-[#0F172A] truncate">
                  {d.label}
                </div>
                <div className="text-[11px] font-mono text-[#1E3A8A] mt-0.5 truncate">
                  {d.route}
                </div>
              </div>
              <ArrowUpRight
                className="w-4 h-4 text-[#1E3A8A] mt-0.5 flex-shrink-0 opacity-70 group-hover:opacity-100"
                strokeWidth={1.75}
              />
            </Link>
          );
        })}
      </div>
    </div>
  );
}
